import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

import type { TestShackReportSectionRevBase } from "@/pages/products/test-shack/models/TestShackReportSectionRev";
import { formatRevision } from "@/pages/products/test-shack/utils/reportFormat";

interface DrawingPreview {
	objectUrl: string | null;
	isError: boolean;
	isPdf: boolean;
	aspectRatio: number | null;
}

interface TestShackReportDrawingFrameProps {
	sectionRev: TestShackReportSectionRevBase;
	label: string;
	/** null when the Section-Rev has no drawing uploaded. */
	preview: DrawingPreview | null;
	onOpen: () => void;
	children?: ReactNode;
}

export const TestShackReportDrawingFrame = ({
	sectionRev,
	label,
	preview,
	onOpen,
	children,
}: TestShackReportDrawingFrameProps) => {
	const pdfAspectRatio =
		preview?.isPdf && preview.objectUrl !== null ? preview.aspectRatio : null;
	const sectionRevLabel = `${sectionRev.sectionName} ${formatRevision(sectionRev.specRev, sectionRev.specSubRev)}`;

	return (
		<Box
			sx={{
				alignSelf: "flex-start",
				flex: { xs: "1 1 auto", lg: `0 0 ${300}px` },
				position: { lg: "sticky" },
				top: 8,
				width: { xs: "100%", lg: 300 },
			}}
		>
			{children}
			<Box
				sx={{
					alignItems: "center",
					backgroundColor: "background.paper",
					border: 1,
					borderColor: "divider",
					borderRadius: 1,
					borderStyle: preview === null ? "dashed" : "solid",
					display: "flex",
					height: pdfAspectRatio ? "auto" : 300,
					justifyContent: "center",
					overflow: "hidden",
					p: 0.5,
				}}
			>
				{preview === null ? (
					<Stack alignItems="center" spacing={0.5} sx={{ px: 2, py: 3 }}>
						<ImageOutlinedIcon sx={{ color: "text.disabled", fontSize: 28 }} />
						<Typography color="text.secondary" sx={{ fontSize: "12px" }}>
							No drawing uploaded
						</Typography>
						<Typography
							color="text.disabled"
							textAlign="center"
							sx={{ fontSize: "11px" }}
						>
							Shared by every inspection of this Section-Rev
						</Typography>
					</Stack>
				) : preview.isError ? (
					<Alert severity="error" role="alert" sx={{ m: 1 }}>
						{label} could not be loaded.
					</Alert>
				) : preview.objectUrl === null ? (
					<Stack alignItems="center" spacing={1} role="status">
						<CircularProgress size={24} />
						<Typography sx={{ fontSize: "12px" }}>
							Loading {label.toLowerCase()}…
						</Typography>
					</Stack>
				) : preview.isPdf ? (
					<Box
						sx={{
							position: "relative",
							height: pdfAspectRatio ? "auto" : "100%",
							lineHeight: 0,
							width: "100%",
						}}
					>
						<Box
							component="iframe"
							src={`${preview.objectUrl}#toolbar=0&view=Fit`}
							title={`${label} PDF for ${sectionRevLabel}`}
							aria-hidden="true"
							tabIndex={-1}
							sx={{
								...(pdfAspectRatio
									? { aspectRatio: pdfAspectRatio, border: 0, width: "100%" }
									: { border: 0, height: "100%", width: "100%" }),
								pointerEvents: "none",
							}}
						/>
						<Box
							component="button"
							type="button"
							aria-label={`Open ${label} full size`}
							title="Open full size"
							onClick={() => onOpen()}
							sx={{
								background: "transparent",
								border: 0,
								cursor: "zoom-in",
								position: "absolute",
								inset: 0,
								p: 0,
								width: "100%",
							}}
						/>
					</Box>
				) : (
					<Box
						component="img"
						src={preview.objectUrl}
						alt={`${label} for ${sectionRevLabel}`}
						title="Open full size"
						onClick={() => onOpen()}
						sx={{
							cursor: "zoom-in",
							maxHeight: "100%",
							maxWidth: "100%",
							objectFit: "contain",
						}}
					/>
				)}
			</Box>
		</Box>
	);
};
